export const TRIP_STATUSES = [
  "Draft",
  "Planned",
  "Dispatched",
  "In Transit",
  "Delivered",
  "Billed",
  "Partially Settled",
  "Settled",
  "Cancelled",
] as const;

export const BOOKING_TYPES = ["Direct", "Broker", "Indirect"] as const;
export const VEHICLE_SOURCES = [
  "Own Vehicle",
  "Market Vehicle",
  "Broker Vehicle",
] as const;
export const PAYMENT_MODES = ["Cash", "UPI", "Bank Transfer", "Other"] as const;
export const EXPENSE_CATEGORIES = [
  "Fuel",
  "FASTag",
  "Driver Bata",
  "Commission",
  "Other",
] as const;
export const AGEING_BUCKETS = ["0-15", "16-30", "31-60", "60+"] as const;

export type TripStatus = (typeof TRIP_STATUSES)[number];
export type LegacyTripStatus = "Completed";
export type BookingType = (typeof BOOKING_TYPES)[number];
export type VehicleSource = (typeof VEHICLE_SOURCES)[number];
export type PaymentMode = (typeof PAYMENT_MODES)[number];
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];
export type AgeingBucket = (typeof AGEING_BUCKETS)[number];
export type DataProtectionState = "protected" | "unprotected" | "unsupported";

export interface BackupMeta {
  lastBackupAt?: string;
  lastDataChangeAt?: string;
}

export type RiskSeverity = "info" | "warning" | "critical";
export type RiskCode =
  | "missingVehicle"
  | "missingParty"
  | "missingBroker"
  | "missingHire"
  | "missingExpense"
  | "overdue"
  | "lowMargin"
  | "expensePressure"
  | "lowAdvance"
  | "transitDelay";

export interface PaymentRecord {
  id: string;
  tripId: string;
  date: string;
  amount: number;
  mode: PaymentMode;
  note: string;
}

export interface ExpenseRecord {
  id: string;
  tripId: string;
  date: string;
  category: ExpenseCategory;
  amount: number;
  note: string;
}

export type PaymentFormRow = Omit<PaymentRecord, "tripId" | "amount"> & {
  amount: string;
};

export type ExpenseFormRow = Omit<ExpenseRecord, "tripId" | "amount"> & {
  amount: string;
};

export interface Trip {
  id: string;
  tripNo: string;
  date: string;
  bookingType: BookingType;
  vehicleSource: VehicleSource;
  brokerName: string;
  vehicleNo: string;
  driverName: string;
  driverMobile: string;
  partyName: string;
  origin: string;
  destination: string;
  hireAmount: number;
  advanceReceived: number;
  fuelExp: number;
  fastagExp: number;
  driverBata: number;
  commissionExp: number;
  otherExp: number;
  totalExpense: number;
  balance: number;
  profit: number;
  status: TripStatus;
  monthKey: string;
  notes: string;
  searchIndex: string;
  settledAt?: string;
  updatedAt?: string;
}

export type TripFormData = {
  tripNo: string;
  date: string;
  bookingType: BookingType;
  vehicleSource: VehicleSource;
  brokerName: string;
  vehicleNo: string;
  driverName: string;
  driverMobile: string;
  partyName: string;
  origin: string;
  destination: string;
  hireAmount: string;
  advanceReceived: string;
  fuelExp: string;
  fastagExp: string;
  driverBata: string;
  commissionExp: string;
  otherExp: string;
  status: TripStatus;
  notes: string;
  payments: PaymentFormRow[];
  expenses: ExpenseFormRow[];
};

export interface TripFinancials {
  totalExpense: number;
  amountReceived: number;
  outstanding: number;
  operatingProfit: number;
  marginPct: number;
  cashCollectedPct: number;
}

export interface TripRisk {
  id: string;
  tripId: string;
  code: RiskCode;
  label: string;
  detail: string;
  severity: RiskSeverity;
  moneyImpact: number;
}

export interface DashboardMetrics {
  totalHire: number;
  totalExpense: number;
  totalProfit: number;
  totalOutstanding: number;
  activeTrips: number;
  lowMarginTrips: number;
  overdueTrips: number;
  deliveredNotBilled: number;
  brokerTrips: number;
  marketVehicleTrips: number;
  dataQualityIssues: number;
}

export interface MonthlyVehicleStats {
  trips: number;
  hire: number;
  expense: number;
  profit: number;
  outstanding: number;
  marginPct: number;
}

export type ContributionStats = MonthlyVehicleStats;

export interface MonthlyData {
  totalHire: number;
  totalExp: number;
  totalProfit: number;
  totalOutstanding: number;
  vehicles: Record<string, MonthlyVehicleStats>;
  parties: Record<string, ContributionStats>;
  routes: Record<string, ContributionStats>;
  brokers: Record<string, ContributionStats>;
}

export type MonthlyDataByKey = Record<string, MonthlyData>;
export type InsightPeriodMode = "month" | "year" | "all";

export interface PartyOutstanding {
  partyName: string;
  trips: number;
  hire: number;
  received: number;
  outstanding: number;
  oldestOpenDate: string;
  ageDays: number;
  ageingBucket: AgeingBucket;
}

export interface TripFilterPreset<TFilters = unknown> {
  id: string;
  name: string;
  filters: TFilters;
  updatedAt: string;
}

export type TripSavePayload = {
  trip: Trip;
  payments: PaymentRecord[];
  expenses: ExpenseRecord[];
};
